import React, { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import Input from 'components/Input'
import Btn from 'components/Btn'
import { Season as SeasonT, Episode as EpisodeT } from './types'

type Mode = 'view' | 'edit'

type BasicInfo = {
  season_number: number | string
  title: string
  description: string
  file?: File | string | null
}

type Props = {
  defaultMode?: Mode
  initialValues?: Partial<BasicInfo>
  season?: SeasonT
  episode?: EpisodeT
  onSave?: (values: BasicInfo) => void
  onCancel?: () => void
}

const Field = ({ label, children }: { label: string, children: React.ReactNode }) => (
  <div className="mb-4">
    <p className="text-sm text-gray-600 mb-2 font-medium">{label}</p>
    {children}
  </div>
)

export default function BasicInformation({
  defaultMode = 'view',
  initialValues,
  season,
  episode,
  onSave,
  onCancel,
}: Props) {
  const [mode, setMode] = useState<Mode>(defaultMode)

  const methods = useForm<BasicInfo>({
    defaultValues: {
      season_number: initialValues?.season_number ?? season?.number ?? 1,
      title: initialValues?.title ?? episode?.title ?? season?.title ?? '',
      description: initialValues?.description ?? episode?.description ?? season?.description ?? '',
      file: initialValues?.file ?? null,
    },
    mode: 'onChange',
  })

  const { handleSubmit, watch, setValue } = methods

  const fileName = useMemo(() => {
    const f = watch('file')
    if (!f) return ''
    if (typeof f === 'string') return f
    if (f instanceof File) return f.name
    return ''
  }, [watch('file')])

  const disabled = mode === 'view'

  const onSubmit = (values: BasicInfo) => {
    onSave?.(values)
    setMode('view')
  }

  const onEditToggle = () => setMode(m => (m === 'edit' ? 'view' : 'edit'))

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) setValue('file', f)
  }

  return (
    <FormProvider {...methods}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-[#0a2342]">Basic information</h1>
          <div className="flex gap-3">
            <Btn
              content={mode === 'edit' ? 'Cancel' : 'Edit'}
              variant={mode === 'edit' ? 'outline' : 'contained'}
              click={mode === 'edit' ? () => setMode('view') : onEditToggle}
              className="px-5"
              disabled={false}
              sx={{}}
              loading={false}
              color={'#0a66c2'}
            />
            {mode === 'edit' && (
              <Btn
                content={'Save changes'}
                click={handleSubmit(onSubmit)}
                className="px-5"
                variant="contained"
                disabled={false}
                sx={{}}
                loading={false}
                color={'#0a66c2'}
              />
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Season number */}
          <Field label="Season number">
            <Input
              name="season_number"
              placeholder="Season 1"
              disabled={disabled}
            />
          </Field>

          {/* Episode title */}
          <Field label="Episode title">
            <Input
              name="title"
              placeholder="Enter episode title"
              disabled={disabled}
            />
          </Field>

          {/* Episode description */}
          <Field label="Episode description">
            <Input
              name="description"
              type="textarea"
              placeholder="Describe the episode…"
              rows={6}
              disabled={disabled}
            />
          </Field>

          {season && mode === 'view' && (
            <div className="grid grid-cols-2 gap-4 my-6">
              <div>
                <p className="text-xs text-gray-500">TV Show</p>
                <p className="text-sm text-gray-900">{season?.tv_show?.title}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Episodes</p>
                <p className="text-sm text-gray-900">{season?.episode_count}</p>
              </div>
            </div>
          )}

          {/* Movie file */}
          <div className="mt-8">
            <p className="text-lg font-semibold mb-3">Movie file</p>
            <div className={`border-2 border-dashed rounded-xl p-6 ${disabled ? 'bg-gray-50' : 'bg-white'} text-center`}>              
              <input
                id="movie-file"
                type="file"
                className="hidden"
                onChange={handleFileSelect}
                disabled={disabled}
                accept=".pdf,image/jpeg,image/png,video/*"
              />
              <label htmlFor="movie-file" className={`cursor-pointer inline-flex items-center gap-2 text-primary ${disabled ? 'pointer-events-none opacity-60' : ''}`}>
                <span className="inline-block w-6 h-6 rounded-full border border-primary flex items-center justify-center">+</span>
                <span>Upload</span>
              </label>
              <p className="text-xs text-gray-500 mt-2">PDF, JPEG, or PNG less than 3MB</p>

              {fileName && (
                <p className="mt-2 text-sm text-green-600">✓ {fileName}</p>
              )}
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  )
}